import * as React from 'react'

/**
 * Rotas sob demanda (Onda 6.2): cada página vira um chunk próprio, e o JS
 * inicial fica só com o shell. Duas coisas que o `React.lazy` puro não faz:
 *
 *  - `preload()`: a rota começa a baixar o chunk junto com as cargas do store,
 *    e não só quando o `CoreGate` libera — sem cascata rede → rede.
 *  - Deploy no meio da sessão: o chunk da versão antiga some do servidor e o
 *    `import()` falha. Recarrega a página uma vez (o `index.html` novo aponta
 *    para os chunks novos); se falhar de novo, o erro sobe para a tela.
 */
export type LazyPage = React.LazyExoticComponent<React.ComponentType> & {
  preload: () => Promise<unknown>
}

const RELOAD_MARK = 'sistemas:recarregou-por-chunk'

const CHUNK_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|ChunkLoadError/i

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return CHUNK_ERROR.test(message)
}

function readMark(): boolean {
  try {
    return window.sessionStorage.getItem(RELOAD_MARK) !== null
  } catch {
    return false
  }
}

function writeMark(on: boolean): void {
  try {
    if (on) window.sessionStorage.setItem(RELOAD_MARK, '1')
    else window.sessionStorage.removeItem(RELOAD_MARK)
  } catch {
    // Sem sessionStorage (modo privado restrito): segue sem a marca.
  }
}

/** Recarrega uma vez por sessão de aba. `true` se a recarga foi pedida. */
export function reloadOnceForChunkError(reload: () => void = () => window.location.reload()): boolean {
  if (readMark()) return false
  writeMark(true)
  reload()
  return true
}

export function lazyPage<M, K extends keyof M>(load: () => Promise<M>, name: K): LazyPage {
  let pending: Promise<{ default: React.ComponentType }> | undefined

  const loadOnce = () => {
    pending ??= load().then(
      (module) => {
        writeMark(false)
        return { default: module[name] as React.ComponentType }
      },
      (error: unknown) => {
        pending = undefined
        // A página vai recarregar: segura o Suspense no "carregando".
        if (isChunkLoadError(error) && reloadOnceForChunkError()) return new Promise<never>(() => undefined)
        throw error
      },
    )
    return pending
  }

  return Object.assign(React.lazy(loadOnce), { preload: loadOnce })
}
